/**
 * Base canónica dos links partilhados (módulo puro, sem rede).
 *
 * Regra: um link partilhado tem de abrir sem sessão para qualquer pessoa. Os
 * hostnames do Preview do Lovable (`*.lovable.app`, incluindo
 * `preview--…`) estão protegidos por login da plataforma — nunca podem
 * aparecer num link enviado a terceiros nem ser guardados na base de dados.
 *
 * Por isso guardamos apenas o token na base de dados e construímos o URL
 * completo no momento da partilha:
 *  - hostname de desenvolvimento local → mantém o próprio origin (testes locais);
 *  - qualquer hostname do Lovable (preview ou publicado) → domínio canónico;
 *  - domínio próprio (routify-tour.com) → normalizado para `www`.
 */

export const CANONICAL_SITE_ORIGIN = "https://www.routify-tour.com";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0", "[::1]"]);

/** Devolve a origem a usar em links partilhados a partir de um origin qualquer. */
export function canonicalShareOrigin(origin?: string | null): string {
  if (!origin) return CANONICAL_SITE_ORIGIN;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return CANONICAL_SITE_ORIGIN;
  }
  const host = url.hostname.toLowerCase();
  if (LOCAL_HOSTS.has(host)) return url.origin;
  if (host.endsWith("routify-tour.com")) return CANONICAL_SITE_ORIGIN;
  // Preview, sandbox e domínios da plataforma: exigem login do Lovable.
  return CANONICAL_SITE_ORIGIN;
}

/** true quando o hostname pertence ao Preview/plataforma (protegido por login). */
export function isPlatformPreviewHost(origin?: string | null): boolean {
  if (!origin) return false;
  try {
    const host = new URL(origin).hostname.toLowerCase();
    return host.endsWith(".lovable.app") || host.endsWith(".lovableproject.com");
  } catch {
    return false;
  }
}

function currentOrigin(): string | null {
  return typeof window === "undefined" ? null : window.location.origin;
}

/** Constrói o URL público de partilha para um caminho já conhecido. */
export function buildShareUrl(path: string, origin?: string | null): string {
  const base = canonicalShareOrigin(origin === undefined ? currentOrigin() : origin);
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

/** URL público de uma rota partilhada (Premium/Free). */
export function buildRouteShareUrl(slug: string, origin?: string | null): string {
  return buildShareUrl(`/r/${slug}`, origin);
}

/** URL público de um roteiro Pro partilhado. */
export function buildProShareUrl(slug: string, origin?: string | null): string {
  return buildShareUrl(`/pro/r/${slug}`, origin);
}
